import { ScaleTime, Selection, EnterElement, ScaleBand, ScaleLinear, zip } from "d3"
import { seriesCanvasLine } from "d3fc"
import { EEGMontageReactCallbacks, SdDetectionReactCallbacks } from "../../../../Types/ReactCallbacks"
import { EEGPage } from "../../../../Data/EEGPage"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { CompositeTimeSeriesData, TimeSeriesData } from "../../../../Data/TimeSeriesData"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { getColors, MobergTheme } from "../../../../../../../../Moberg"
import { LABEL_SETTINGS } from "../../../../../../../../Constants/LabelSettings"
import { PageRectangle } from "../../../../Types/Trace"
import { sortedIndex } from "lodash"
import { areAllModalitiesRendered, getModalityRenderEndTimes } from "./EEGRenderCache"

export type EEGCanvasConfig = {
	graphId: string
	viewScale: ScaleTime<any, any, any>
	channelScale: ScaleBand<string>
	sensitivityScaleVoltsToPixels: ScaleLinear<any, any, any>
	ecgSensitivityScaleVoltsToPixels?: ScaleLinear<any, any, any>
	eegModality: string | undefined
	dataObjectId: number
	color?: string
	ecgColor?: string
	ecgChannelKeys?: Set<string>
	ecgSensitivity?: number
	calibrationToken?: string
	hiddenChannelKeys: Set<string>
	spacerChannelKeys?: Set<string>
}

export class D3EEGCanvas extends D3OneToOneRenderable<SVGGElement, SVGForeignObjectElement, EEGCanvasConfig, EEGMontageReactCallbacks | SdDetectionReactCallbacks> {
	private offscreenRenderer = seriesCanvasLine()
	private context?: CanvasRenderingContext2D | null
	private pageManager: TimeSeriesPageManager<EEGPage>
	private offscreenCanvas: OffscreenCanvas
	private offscreenXScale: ScaleTime<any, any, any>
	private offscreenCanvasContext: OffscreenCanvasRenderingContext2D | null
	private pageRectangle = { x: 0, y: 0, width: 0, height: 0 }
	private placeholderColor: string = ""
	private traceColor: string = ""
	private colorSchemeObserver: MutationObserver


	constructor(root: SVGGElement, config: EEGCanvasConfig, pageManager: TimeSeriesPageManager<EEGPage>, reactCallbacks: EEGMontageReactCallbacks | SdDetectionReactCallbacks) {
		super(root, config, "d3-eeg-canvas", reactCallbacks)
		this.pageManager = pageManager

		const width = config.viewScale.range()[1]
		const height = config.channelScale.range()[1]
		this.offscreenCanvas = new OffscreenCanvas(width, height)
		this.offscreenCanvasContext = this.offscreenCanvas.getContext("2d")
		this.offscreenXScale = config.viewScale.copy().range([0, width])

		this.offscreenRenderer = seriesCanvasLine()
			.xScale(this.offscreenXScale)
			.yScale(this.config.channelScale)
			.crossValue((p: [number, number]) => p[0])
			.mainValue((p: [number, number]) => p[1])
			.context(this.offscreenCanvas.getContext("2d"))

		this.readThemeColors()
		this.colorSchemeObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.attributeName === "data-color-scheme") {
					this.readThemeColors()
					this.render()
					break
				}
			}
		})
		this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

		this.render()
	}

	public destroy() {
		this.colorSchemeObserver.disconnect()
	}

	// Resolving CSS variables through getComputedStyle forces a style recalculation, so the
	// theme colors are read once per color scheme rather than once per channel per draw.
	private readThemeColors() {
		const rootStyle = getComputedStyle(document.documentElement)
		this.placeholderColor = rootStyle.getPropertyValue("--color-chart-placeholder").trim() || getColors(MobergTheme.GRAY).main
		this.traceColor = rootStyle.getPropertyValue("--color-chart-axis-text").trim() || "black"
	}

	public renderPage = (page: EEGPage | undefined) => {
		if (!page || !this.context || !this.config.eegModality || !this.offscreenCanvasContext) {
			return
		}

		const pageRectangle = this.getPageRectangle(page.startTime, page.endTime)
		const { x, y, width, height } = pageRectangle
		const renderModalities = this.getRenderModalities(this.config.eegModality)

		this.context.clearRect(x, y, width, height)

		const { isRenderComplete, cacheEndTimes } = this.drawRenderCache(page, pageRectangle, renderModalities)

		if (isRenderComplete) {
			return
		}

		this.drawNewData(page, pageRectangle, cacheEndTimes)

		const bitmap = this.offscreenCanvas.transferToImageBitmap()
		const endTimes = getModalityRenderEndTimes(page.responseBuffers, renderModalities, page.startTime)

		page.updateRenderCache(this.config.dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges: [], endTimes })

		this.context.drawImage(bitmap, x, y)
		this.drawUnloadedRegion(page, endTimes[this.config.eegModality])
	}

	private drawRenderCache = (
		page: EEGPage,
		pageRectangle: PageRectangle,
		renderModalities: string[]
	): { isRenderComplete: boolean, cacheEndTimes: Record<string, number> } => {
		if (!this.context || !this.offscreenCanvasContext) {
			return { isRenderComplete: false, cacheEndTimes: {} }
		}

		const { x, y } = pageRectangle

		// Check if there is a cached render at the same resolution
		const cachedRender = page.renderCache.get(this.config.dataObjectId)?.get(this.getRenderCacheKey())

		if (
			cachedRender
			&& !cachedRender.dirty
			&& cachedRender.bitmap.width === this.context.canvas.width
			&& cachedRender.bitmap.height === this.context.canvas.height
		) {
			const cacheEndTimes = cachedRender.endTimes ?? {}
			const bitmap = cachedRender.bitmap

			// We already have a clean render cache that contains the full page of data, so there's no more work to do.
			if (areAllModalitiesRendered(cacheEndTimes, renderModalities, page.endTime)) {
				this.context.drawImage(bitmap, x, y)
				return { isRenderComplete: true, cacheEndTimes }
			}

			// Render to the offscreen canvas so we can reuse the image we already drew
			this.offscreenCanvasContext.drawImage(bitmap, 0, 0)
			return { isRenderComplete: false, cacheEndTimes }
		}

		return { isRenderComplete: false, cacheEndTimes: {} }
	}

	private drawNewData = (page: EEGPage, pageRectangle: PageRectangle, cacheEndTimes: Record<string, number>) => {
		if (!this.context || !this.config.eegModality) {
			return
		}

		const { width } = pageRectangle
		const pageData = page.data.get(this.config.dataObjectId)
		const montageData = pageData?.get(this.config.eegModality) as CompositeTimeSeriesData | undefined

		const spacerKeys = this.config.spacerChannelKeys ?? new Set<string>()
		const ecgKeys = this.config.ecgChannelKeys ?? new Set<string>()
		const montageStartTime = cacheEndTimes[this.config.eegModality]
		const unrenderedMontageDataStartIndex = montageStartTime !== undefined && montageData
			? sortedIndex(montageData.times, montageStartTime)
			: 0

		let eegDataIndex = 0

		this.offscreenXScale.domain([page.startTime, page.endTime]).range([0, width])

		for (const channelPair of this.config.channelScale.domain()) {
			let channelData: Float32Array | undefined
			let channelTimes: number[] | undefined
			let unrenderedDataStartIndex: number = 0

			if (spacerKeys.has(channelPair)) {
				continue
			}

			const isECG = ecgKeys.has(channelPair)

			if (isECG) {
				const ecgData = pageData?.get(channelPair) as TimeSeriesData | undefined
				channelData = ecgData?.data
				channelTimes = ecgData?.times
				const ecgStartTime = cacheEndTimes[channelPair]
				unrenderedDataStartIndex = ecgStartTime !== undefined ? sortedIndex(ecgData?.times, ecgStartTime) : 0
			} else {
				channelData = montageData?.data[eegDataIndex]
				channelTimes = montageData?.times
				unrenderedDataStartIndex = unrenderedMontageDataStartIndex
				eegDataIndex += 1
			}

			if (!channelData || !channelTimes || unrenderedDataStartIndex >= channelData.length || this.config.hiddenChannelKeys.has(channelPair)) {
				continue
			}

			const center = (this.config.channelScale(channelPair) as number) + this.config.channelScale.step() / 2
			const baseSensitivityScale = (
				isECG
					? this.config.ecgSensitivityScaleVoltsToPixels
					: this.config.sensitivityScaleVoltsToPixels
			) ?? this.config.sensitivityScaleVoltsToPixels
			const sensitivityScale = isECG
				? baseSensitivityScale.copy().domain(baseSensitivityScale.domain().map(value => -value))
				: baseSensitivityScale
			const newRange = [sensitivityScale.range()[0] + center, sensitivityScale.range()[1] + center]
			const newYScale = sensitivityScale.copy().range(newRange)
			const color = isECG
				? (this.config.ecgColor ?? LABEL_SETTINGS["ECG_II"].color)
				: this.config.color ?? this.traceColor

			this.offscreenRenderer.yScale(newYScale)
			this.offscreenRenderer.decorate((context: CanvasRenderingContext2D) => {
				context.strokeStyle = color
			})
			this.offscreenRenderer(this.getRenderableTraceData(channelTimes, channelData, unrenderedDataStartIndex))
		}
	}

	private getRenderableTraceData = (times: number[], values: Float32Array, startIndex: number): number[][] => {
		if (startIndex === 0) {
			return zip(times, values)
		}

		const result: number[][] = new Array(times.length - startIndex)

		for (let i = startIndex; i < times.length; i++) {
			result[i - startIndex] = [times[i], values[i]]
		}

		return result
	}

	private drawUnloadedRegion = (page: EEGPage, unloadedStartTime: number | undefined) => {
		const rect = this.getUnloadedRectangle(page, unloadedStartTime)

		if (!this.context || !rect) {
			return
		}

		this.context.fillStyle = this.placeholderColor
		this.context.fillRect(rect.x, rect.y, rect.width, rect.height)
	}

	// The placeholder follows the EEG montage only. ECG rows are independent, and hiding
	// drawn EEG until ECG catches up would misrepresent what has loaded.
	private getUnloadedRectangle = (page: EEGPage, unloadedStartTime: number | undefined): PageRectangle | null => {
		if (!this.context || !unloadedStartTime) {
			return null
		}

		const x1 = this.config.viewScale(unloadedStartTime)
		const x2 = this.config.viewScale(page.endTime)
		const height = this.config.channelScale.range()[1]

		return { x: x1, y: 0, width: x2-x1, height: height }
	}

	// Every modality that contributes to the cached bitmap must reach the page end before the
	// cache counts as complete, otherwise an ECG response arriving after EEG is never drawn.
	private getRenderModalities = (eegModality: string): string[] => {
		const modalities = new Set<string>([eegModality])

		this.config.ecgChannelKeys?.forEach(modality => {
			if (!this.config.hiddenChannelKeys.has(modality)) {
				modalities.add(modality)
			}
		})

		return Array.from(modalities)
	}

	private getRenderCacheKey = () => {
		const hiddenKeysToken = Array.from(this.config.hiddenChannelKeys).sort().join("|") || "visible"
		const colorScheme = document.documentElement.getAttribute("data-color-scheme") ?? "light"
		const domainToken = this.config.channelScale.domain().join(",")
		return `${this.config.graphId}-${domainToken}-${this.config.eegModality}-${this.config.ecgSensitivity ?? "none"}-${this.config.calibrationToken ?? "uncalibrated"}-${hiddenKeysToken}-${colorScheme}`
	}

	private getPageRectangle = (startTime: Date | number, endTime: Date | number) => {
		const x1 = this.config.viewScale(startTime)
		const x2 = this.config.viewScale(endTime)
		const width = x2 - x1
		const height = this.config.channelScale.range()[1]

		this.pageRectangle.x = Math.round(x1)
		this.pageRectangle.width = width
		this.pageRectangle.height = height

		return this.pageRectangle
	}

	protected updateDerivedState = () => {
		this.offscreenCanvas.width = this.config.viewScale.range()[1]
		this.offscreenCanvas.height = this.config.channelScale.range()[1]
	}

	protected enter = (newTrace: Selection<EnterElement, any, any, any>): Selection<SVGForeignObjectElement, any, any, any> => {
		const foreignObject = newTrace.append("foreignObject").attr("class", this.className)
		const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, any, any, any>

		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]
		const height = this.config.channelScale?.range()[1] - this.config.channelScale?.range()[0]

		foreignObject.attr("width", width).attr("height", height)
		canvas.attr("width", width).attr("height", height)

		const canvasNode = canvas.node()

		if (canvasNode != null) {
			this.context = canvasNode.getContext("2d") ?? undefined

			// Show a blank gray screen on the first render
			if (this.context) {
				this.context.fillStyle = this.placeholderColor
				this.context.fillRect(0, 0, width, height)
			}
		}

		this.pageManager.getPagesInView().forEach(page => this.renderPage(page))

		return foreignObject
	}

	protected update = (updatedForeignObject: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const updatedCanvas = updatedForeignObject.select("canvas")

		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]
		const height = this.config.channelScale?.range()[1] - this.config.channelScale?.range()[0]

		updatedForeignObject.attr("width", width).attr("height", height)
		updatedCanvas.attr("width", width).attr("height", height)

		this.pageManager.getPagesInView().forEach(page => this.renderPage(page))

		return updatedForeignObject
	}
}
